/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/styles';
import classnames from 'classnames';
import clsx from 'clsx';
import {getAssetState, getAssetStateLabel} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import {BaseAsset} from '@ion/core/src/types/entities';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {isNonZeroNumber} from '@ion/core/src/utils/NumberUtil';
import useRootManager from '@ion/dls/src/client/useRootManager';
import ChargeLevel from '@ion/dls/src/components/chargelevel/ChargeLevel';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {TooltipWrapper} from '@ion/dls/src/components/tooltip/TooltipWrapper';
import {AssetStatusIcon} from '@ion/dls/src/icons/AssetStatusIcon';
import {PoorGpsIcon} from '@ion/dls/src/icons/PoorGpsIcon';
import {SystemDateTimeIcon} from '@ion/dls/src/icons/SystemDateTimeIcon';
import Address from '@ion/dls/src/maps/components/Address';
import {getSCStatus} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {BodyTypographyVariantEnum} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {formatAddress, getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import FindNearByPopupContent, {OnFindNearByProps} from '../FindNearByPopupContent';

export interface DeviceDetailPopupProps {
  selectedDevice: any;
  showFindNearBy: boolean;
  onFindNearBySubmit?: (props: OnFindNearByProps) => void;
  onCancelFindNearBy: () => void;
}

const useStyles = makeStyles(() => ({
  popupContentWrapper : {
    width   : '100%',
    padding : '0px 8px'
  },
  chargeFuelContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    alignItems     : 'center',
    justifyContent : 'center'
  },
  statusContainer : {
    height          : 30,
    width           : 258,
    display         : 'flex',
    alignItems      : 'center',
    padding         : '0 4px 0 12px',
    justifyContent  : 'space-between',
    color           : dlsColor('white'),
    backgroundColor : dlsColor('grey3'),
    fontSize        : '12px !important'
  },
  statusIconContainer : {
    ...dlsAlt('typography', 'cta2'),
    display    : 'flex',
    gap        : 5,
    alignItems : 'center'
  },
  status : {
    fontWeight : 600
  },
  width100 : {
    width : '100%'
  },
  center : {
    display    : 'flex',
    alignItems : 'center',
    marginLeft : 5
  },
  eventTimeContainer : {
    ...dlsAlt('typography', 'label3'),
    fontSize   : '11px !important',
    lineHeight : '18px !important',
    fontWeight : 700,
    color      : dlsColor('grey2')
  },
  eventTime : {
    display : 'flex',
    width   : '100%',
    gap     : 6
  },
  addressContainer : {
    color         : `${dlsColor('grey3')} !important`,
    textTransform : 'uppercase'
  },
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'addressTooltip')
  },
  iconSpan : {
    display     : 'flex',
    position    : 'relative',
    top         : 1,
    marginRight : 3
  },
  nameContainer : {
    color         : dlsColor('aaBlue'),
    textTransform : 'uppercase',
    height        : 24,
    lineHeight    : '24px !important'
  },
  fontContent : {
    fontWeight : 700,
    fontSize   : 12,
    lineHeight : '16px'
  },
  subText : {
    color         : dlsColor('grey3'),
    margin        : '0 !important',
    padding       : '0 0 2px !important',
    fontSize      : '10px !important',
    fontWeight    : 500,
    textTransform : 'uppercase',
    marginLeft    : '4px !important'
  },
  hideText : {
    visibility : 'hidden'
  }
}));

/**
 * DeviceDetailPopup
 *
 * Displays detailed information about a selected SC device within the map popup.
 * The component shows the device status, battery level, name/asset association,
 * last event time, and location address. It also provides visual indicators such
 * as GPS quality and status icons.
 *
 * When enabled, the popup can switch to the "Find Nearby" mode, allowing the user
 * to search for nearby assets/devices using the FindNearByPopupContent component.
 * 
 * ```typescript
 *   import DeviceDetailPopup from '@/routes/tracking/maps/DeviceDetailPopup';
 *
 *   <DeviceDetailPopup selectedDevice={Device} showFindNearBy={false} onFindNearBySubmit={function(){}}/>
 *  ```
 */
const DeviceDetailPopup = ({
  selectedDevice,
  showFindNearBy,
  onFindNearBySubmit,
  onCancelFindNearBy
}: DeviceDetailPopupProps) => {
  const styles                                = useStyles();
  const [showToolTip, setShowToolTip]         = useState(false);
  const [showIconToolTip, setShowIconToolTip] = useState(false);
  const {getContainer}                        = useRootManager(false);
  const {mode}                                = useThemeMode();

  const {
    assetName,
    deviceName,
    street,
    state,
    city,
    eventTime,
    status,
    batteryLevelValue,
    latitude,
    longitude,
    gpsValidity
  } = selectedDevice || {};

  const isAssetNameAvailable = useMemo(() => !!assetName && assetName?.trim?.().length > 0, [assetName]);
  const name = useMemo(() => {
    if (isAssetNameAvailable) {
      return assetName?.trim?.();
    }
    return deviceName?.trim();
  }, [assetName, isAssetNameAvailable, deviceName]);

  const dateTime   = getFormattedDate(eventTime).toUpperCase();
  const statusText = getAssetStateLabel(getAssetState(selectedDevice));
  const statusIcon = getSCStatus(status, selectedDevice as unknown as BaseAsset);
  
  const statusSvgStyle = useMemo(() => ({
    border       : `1px solid ${dlsColor('white')}`,
    height       : 12,
    width        : 12,
    borderRadius : '50%',
    borderWidth  : '2px'
  }), [mode]);

  useEffect(() => {
    return () => {
      setShowToolTip(false);
      setShowIconToolTip(false);
    };
  }, []);

  const setToolTip = useCallback(({type}) => {
    setShowToolTip(!(type === 'click' || type === 'mouseleave'));
  }, [showToolTip]);

  const setIconToolTip = useCallback(({type}) => {
    setShowIconToolTip(!(type === 'click' || type === 'mouseleave'));
  }, [showIconToolTip]);

  const addressTooltip = useMemo(() => {
    const isValidGPS = isNonZeroNumber(latitude) && isNonZeroNumber(longitude);
    if (isValidGPS) {
      const title = formatAddress({street, city, state});
      const poorGpsTooltip = translate('tracking:list.detailed.poorGpsQuality');
      return (
        <div id='sc-device-detail-popup' className={clsx([styles.width100, styles.center])}>
          {!isTrue(gpsValidity) && (
            <Tooltip id='sc-device-detail-gps-popup'
              title={showIconToolTip && poorGpsTooltip}
              open={showIconToolTip}
              placement={'top'}
              arrow >
              <span className={clsx([styles.iconSpan])}
                onMouseEnter={setIconToolTip}
                onMouseLeave={setIconToolTip}
                onClick={setIconToolTip}>
                <PoorGpsIcon />
              </span>
            </Tooltip>
          )}
    
          <TooltipWrapper title={showToolTip && title}
            container={getContainer('base-container')}
            open={showToolTip}>
            <div onMouseEnter={setToolTip}
              onMouseLeave={setToolTip}
              onClick={setToolTip}
              className={clsx([styles.width100, styles.addressContainer])}>
              <Address street={street}
                state={state}
                city={city}
                isPopupVariant />
            </div>
          </TooltipWrapper>
        </div>
      );
    }
  }, [street, state, city, showToolTip, showIconToolTip, latitude, longitude]);
  
  return (
    <div style={{width: '100%'}}
      onMouseLeave={setToolTip}
      onClick={setToolTip} >
      {!showFindNearBy && (
        <>
          <div className={clsx([styles.statusContainer])}>
            <span className={styles.statusIconContainer}>
              <AssetStatusIcon status={statusIcon} style={statusSvgStyle}/>
              <span className={styles.status}>
                {statusText?.toUpperCase()}
              </span>
            </span>
            <span className={styles.chargeFuelContainer}>
              <ChargeLevel charge={Number(batteryLevelValue)} />
            </span>
          </div>
          <div className={styles.popupContentWrapper}>
            <BodyTypography variant={BodyTypographyVariantEnum.body2}
              id={'sc-device-popup-device-name'}
              className={classnames(styles.nameContainer, styles.fontContent)}>
              {name}
            </BodyTypography>
            <LabelTypography variantType='label2'
              className={classnames(styles.subText, {
                [styles.hideText] : !isAssetNameAvailable
              })}
              id={'sc-device-popup-device-id'}>
              {deviceName?.trim()}
            </LabelTypography>
            <LabelTypography variantType='label2'
              className={classnames(styles.eventTimeContainer, styles.eventTime)}
              id={'sc-device-popup-device-eventTime'}>
              <SystemDateTimeIcon color={dlsColor('grey1')} />
              {dateTime}
            </LabelTypography>
            {addressTooltip}
          </div>
        </>
      )}
      {showFindNearBy && (
        <FindNearByPopupContent asset={selectedDevice}
          onFindButtonClick={onFindNearBySubmit}
          onCancel={onCancelFindNearBy} />
      )}
    </div>
  );
};

export default DeviceDetailPopup;
